import { Component, Inject, Input, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomerDto, CustomerListDto } from '@careapp-group/ng-sdk';
import { Store } from '@ngrx/store';
import * as selectors from '../../store/reducers/index';
import { Observable, Subscription } from 'rxjs';
import { getIsFamily } from '../../store/organisations/organisations.selectors';
import { CUSTOMER_FAMILY_ROLE } from '@careapp-group/core';

export interface CustomerSelectDialogData {
  hideUnassigned: boolean;
  multi: boolean;
  disableNoMedia: boolean;
  disableWhereFamilyRole: boolean;
  selectedIds: string[];
  selectedCustomerListIds: string[];
  initialTab: 'all' | 'my' | 'lists';
  adminRoute?: boolean;
}

export interface CustomerSelectDialogResponse {
  selectedIds: string[];
  selectedCustomerListIds: string[];
}

@Component({
    selector: 'ca-customer-select-dialog',
    templateUrl: './customer-select-dialog.component.html',
    styleUrls: ['./customer-select-dialog.component.scss'],
    standalone: false
})
export class CustomerSelectDialogComponent implements OnDestroy {
  private _customerListEntities: { [id: string]: CustomerListDto } = {};
  public customerLists$: Observable<{ [id: string]: CustomerListDto }>;
  public isFamily$: Observable<boolean>;
  public customerListsSubscription: Subscription;
  @Input() adminRoute = false;
  @Input() multi;
  @Input() disableNoMedia: boolean;
  // Do not allow selection of customers where the user only has a customer_family role.
  @Input() disableWhereFamilyRole: boolean;
  @Input() hideUnassigned: boolean;
  @Input()
  public selectedIdPanelOpenState = true;
  private _selectedIds: string[];
  public get selectedIds(): string[] {
    return this._selectedIds ?? [];
  }
  public set selectedIds(value: string[]) {
    if (!value || !Array.isArray(value)) {
      this.selectedIds = [];
    } else {
      // Copy to avoid mutation
      this._selectedIds = [...value];
    }
  }
  @Input()
  private _selectedCustomerListIds: string[];
  public get selectedCustomerListIds(): string[] {
    return this._selectedCustomerListIds;
  }
  public set selectedCustomerListIds(value: string[]) {
    if (!value || !Array.isArray(value)) {
      this.selectedCustomerListIds = [];
    } else {
      // Copy to avoid mutation
      this._selectedCustomerListIds = [...value];
    }
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CustomerSelectDialogData,
    private store: Store<any>,
    private dialogRef: MatDialogRef<CustomerSelectDialogComponent>
  ) {
    this.multi = data.multi;
    this.disableNoMedia = data.disableNoMedia;
    this.disableWhereFamilyRole = data.disableWhereFamilyRole;
    this.hideUnassigned = data.hideUnassigned;
    this.selectedIds = data.selectedIds || [];
    this.selectedCustomerListIds = data.selectedCustomerListIds || [];
    this.adminRoute = data.adminRoute || false;

    this.customerLists$ = this.store.select(selectors.getEntities(selectors.CUSTOMER_LISTS));
    this.isFamily$ = this.store.select(getIsFamily);

    this.customerListsSubscription = this.customerLists$.subscribe(
      (data) => (this._customerListEntities = data)
    );
  }

  ngOnDestroy() {
    this.customerListsSubscription.unsubscribe();
  }

  pruneEmptyCustomerLists() {
    this.selectedCustomerListIds = [...this.selectedCustomerListIds].filter((customerListId) =>
      this._customerListEntities[customerListId].customerIds.some((customerId) =>
        this.selectedIds.includes(customerId)
      )
    );
  }

  toggle(customer: CustomerDto) {
    if (this.multi) {
      this.selectedIds = !!this.selectedIds.find((id) => id === customer.id)
        ? this.selectedIds.filter((id) => id !== customer.id)
        : [...this.selectedIds, customer.id];

      // Remove any customer lists where there no longer exists any selected customers
      this.pruneEmptyCustomerLists();
    } else {
      this.selectedIds = [customer.id];
    }
  }

  toggleCustomerList(customerList: CustomerListDto) {
    // Being a short-hand for multiple customers, multi must be true for customer lists
    if (this.multi) {
      let newSelectedIds = [...this.selectedIds];
      let newSelectedCustomerListIds = [...this.selectedCustomerListIds];

      if ((this.selectedCustomerListIds ?? []).includes(customerList.id)) {
        // Remove from list
        newSelectedIds = newSelectedIds.filter((id) => !customerList.customerIds.includes(id));
        newSelectedCustomerListIds = this.selectedCustomerListIds.filter(
          (id) => id !== customerList.id
        );
        for (let i = 0; i < newSelectedCustomerListIds.length; i++) {
          const customerListId = newSelectedCustomerListIds[i];
          const customerList = this._customerListEntities[customerListId];
          if (customerList != null) {
            newSelectedIds = Array.from(new Set([...newSelectedIds, ...customerList.customerIds]));
          }
        }
      } else {
        // Add to list
        newSelectedIds = Array.from(new Set([...this.selectedIds, ...customerList.customerIds]));
        newSelectedCustomerListIds = Array.from(
          new Set([...this.selectedCustomerListIds, customerList.id])
        );
      }

      this.selectedIds = newSelectedIds;
      this.selectedCustomerListIds = newSelectedCustomerListIds;
    }
  }

  // For passing into the filter pipe,
  // allowMemoImages: Provide the value allowMemoImages should be for the customer. (null to ignore)
  // disableWhereFamilyRole: Filters a customer out if the user does not have a customer_family role for them.
  // If either input is null, will not filter by that input.
  customerFilter(allowMemoImages?: boolean, disableWhereFamilyRole?: boolean) {
    return (item: CustomerDto) => {
      // Early exit: Nothing to filter.
      if (allowMemoImages == null && disableWhereFamilyRole == null) {
        return true;
      }

      if (item) {
        // Filter out customers where allowMemoImages is not the same as the input.
        if (allowMemoImages != null) {
          return item.settings.allowMemoImages == allowMemoImages;
        }

        // Filter out customers where the user does not have a customer_family role.
        if (
          disableWhereFamilyRole &&
          item.permissions.some((p) => p.role == CUSTOMER_FAMILY_ROLE)
        ) {
          return false;
        }
      }

      return true;
    };
  }

  submitAndClose(selectedCustomerEntities: CustomerDto[]) {
    if (this.disableNoMedia === true) {
      this.selectedIds = selectedCustomerEntities
        .filter((customer) => customer.settings.allowMemoImages !== false)
        .map((customer) => customer.id);

      // Remove any customer lists where there no longer exists any selected customers
      this.pruneEmptyCustomerLists();
    }

    this.dialogRef.close({
      selectedIds: this.selectedIds,
      selectedCustomerListIds: this.selectedCustomerListIds,
    } as CustomerSelectDialogResponse);
  }
}
